import { Progress } from 'antd';
import { t } from 'ttag';
import './pdfLoading.scss';
import type { PDFLoadingProps } from './types';

/**
 * Renders a second count as an 'h hrs m min s sec' readout for ECG playback and event timings. Splits the total into hours, minutes and seconds and appends the larger units only when they are non-zero, returning '...' for a falsy value.
 *
 * @param timeInSeconds - Elapsed time in seconds.
 * @returns Composed time string, or '...' when no value was given.
 */
const getTimeString = (timeInSeconds?: number) => {
  if (timeInSeconds) {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor(timeInSeconds / 60 - hours * 60);
    const seconds = Math.floor(timeInSeconds - minutes * 60 - hours * 3600);
    let timeString = `${seconds} sec`;
    if (minutes) {
      timeString = `${minutes} min ${timeString}`;
    }
    if (hours) {
      timeString = `${hours} hrs ${timeString}`;
    }
    return timeString;
  }
  return '...';
};

/**
 * Progress indicator shown while a report PDF downloads. Renders a circular Progress at the rounded download percentage above a Loading report label and an estimated-time-left line formatted by getTimeString.
 *
 * @param props - The download progress fraction and the estimated remaining time.
 * @returns The rendered loading indicator element.
 */
const PDFLoading = (props: PDFLoadingProps) => (
  <div className="pdf-loading-container">
    <Progress
      type="circle"
      percent={Math.round((props.downloadProgress ?? 0) * 100)}
      strokeColor="#07A880"
      strokeLinecap="square"
      size={[80, 80]}
    />
    <div className="pdf-loading__text">{t`Loading report`}</div>
    <div className="pdf-loading__estimated-text">
      {`Estimated time left: ${getTimeString(props.downloadEstimated ?? 0)}`}
    </div>
  </div>
);

export default PDFLoading;
